// utils/supabase/client.ts
import { createBrowserClient } from '@supabase/ssr'
import type { SupabaseClient } from '@supabase/supabase-js'

// Singleton client instance to avoid multiple GoTrueClient instances
let supabaseClient: SupabaseClient | null = null;
let hasWarnedAboutIframeStorage = false;

// Check if we're in an iframe (cross-origin context where cookies may be blocked)
function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to cross-origin restrictions, we're in an iframe
    return true;
  }
}

function resolveSupabaseBrowserConfig(): { url: string; key: string } | null {
  const isLocalDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const devUrl = process.env.NEXT_PUBLIC_SUPABASE_URL_DEV;
  const devKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_DEV;
  const prodUrl = process.env.NEXT_PUBLIC_SUPABASE_URL_PROD;
  const prodKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_PROD;
  const fallbackUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const fallbackKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  const selectedUrl = isLocalDev ? devUrl || fallbackUrl : prodUrl || fallbackUrl;
  const selectedKey = isLocalDev ? devKey || fallbackKey : prodKey || fallbackKey;

  if (!selectedUrl || !selectedKey) {
    return null;
  }

  return { url: selectedUrl, key: selectedKey };
}

export function hasSupabaseBrowserConfig(): boolean {
  return resolveSupabaseBrowserConfig() !== null;
}

export function createClient() {
  // Return existing singleton if available
  if (supabaseClient) {
    return supabaseClient;
  }

  const config = resolveSupabaseBrowserConfig();
  if (!config) {
    console.error(
      '[Supabase Client] Missing public Supabase env vars. Set NEXT_PUBLIC_SUPABASE_URL_DEV + NEXT_PUBLIC_SUPABASE_ANON_KEY_DEV for localhost (or NEXT_PUBLIC_SUPABASE_URL + NEXT_PUBLIC_SUPABASE_ANON_KEY fallback). For deployed environments, set _PROD or fallback base vars.'
    );
    // Keep runtime alive with deterministic fallback for non-auth pages.
    supabaseClient = createBrowserClient('https://invalid.supabase.local', 'invalid-anon-key');
    return supabaseClient;
  }

  const { url, key } = config;

  // In iframe context, use localStorage for session storage since cookies are blocked
  // This is needed for GHL embedded app to work.
  // Safari ITP throws SecurityError on localStorage access inside cross-origin iframes,
  // so we wrap it in a try-catch adapter to fall back to in-memory gracefully.
  const inIframe = isInIframe();

  if (inIframe) {
    console.log('[Supabase Client] Running in iframe, using safe localStorage adapter for auth');
    const warnStorageBlocked = (operation: string, error?: unknown) => {
      if (hasWarnedAboutIframeStorage) return;
      hasWarnedAboutIframeStorage = true;
      console.warn('[Supabase Client] iframe storage blocked, falling back to degraded persistence', {
        operation,
        error: error instanceof Error ? error.message : String(error ?? 'unknown'),
      });
    };

    const safeStorage: Storage = {
      get length() {
        try { return window.localStorage.length; } catch { return 0; }
      },
      key(index: number) {
        try { return window.localStorage.key(index); } catch { return null; }
      },
      getItem(key: string) {
        try { return window.localStorage.getItem(key); } catch (error) { warnStorageBlocked('getItem', error); return null; }
      },
      setItem(key: string, value: string) {
        try { window.localStorage.setItem(key, value); } catch (error) { warnStorageBlocked('setItem', error); }
      },
      removeItem(key: string) {
        try { window.localStorage.removeItem(key); } catch (error) { warnStorageBlocked('removeItem', error); }
      },
      clear() {
        try { window.localStorage.clear(); } catch (error) { warnStorageBlocked('clear', error); }
      },
    };
    supabaseClient = createBrowserClient(url, key, {
      auth: {
        storage: safeStorage,
        storageKey: 'sb-auth-token',
        flowType: 'pkce',
        detectSessionInUrl: true,
        persistSession: true,
      },
    });
  } else {
    // Create and cache the singleton instance with default cookie storage
    supabaseClient = createBrowserClient(url, key);
  }
  
  return supabaseClient;
}
